import styled from '@emotion/styled';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { IconArrowRight } from '../../common/design-tokens/Icons';
import { validateEmail } from '../../common/utils';
import { t } from '../../localization/LocaleContext';
import { useForgotPassword } from '../../remoteData/unauthenticated';
import FloatingLabelInput from '../shared/FloatingLabelInput';
import {
  Container,
  Email,
  InputContainer,
  Inputs,
  SubmitButton,
  Text,
  Title,
} from './styledComponents';

const SubmitIcon = styled(IconArrowRight)`
  svg {
    path {
      fill: white;
    }
  }
`;

const ErrorBlock = styled.p`
  ${({ theme }) => theme.textStyles.paragraphS};
  color: ${({ theme }) => theme.colors.brandCoral100};
  margin-top: ${({ theme }) => theme.spacers.xl};
`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');

  const [validEmailAddress, setValidEmailAddress] = useState<
    boolean | undefined
  >(undefined);

  const intl = useIntl();

  useAnonymousAnalyticsTrackPage({
    name: 'Forgot Password',
    category: 'Public',
  });

  function validateEmailAddress(passedEmail: string) {
    if (passedEmail === '') {
      setValidEmailAddress(undefined);
    } else {
      setValidEmailAddress(validateEmail(passedEmail));
    }
  }

  const {
    mutate: forgotPassword,
    error: forgotPasswordError,
    isSuccess: forgotPasswordSuccess,
  } = useForgotPassword();

  const errorsInForm = !validEmailAddress;

  if (forgotPasswordSuccess) {
    return (
      <Container narrow>
        <Title>{t('forgot_password_success_title')}</Title>
        <Text>
          {t('forgot_password_success_text', { email: <Email>{email}</Email> })}
        </Text>
      </Container>
    );
  }

  return (
    <Container narrow>
      <Title>{t('forgot_password_title')}</Title>
      <Text>{t('forgot_password_text')}</Text>

      <form
        method="post"
        action="/api/account/forgot-password"
        onSubmit={(event) => {
          event.preventDefault();
          forgotPassword({ email });
        }}
      >
        <Inputs>
          <InputContainer>
            <FloatingLabelInput
              name="email"
              value={email}
              error={
                validEmailAddress === false
                  ? intl.formatMessage({
                      id: 'general_invalid_email',
                    })
                  : undefined
              }
              type="text"
              label={intl.formatMessage({
                id: 'general_email_fieldtext',
              })}
              onChange={(event) => {
                setEmail(event.target.value);
                validateEmailAddress(event.target.value);
              }}
              inputProps={{ autoComplete: 'email' }}
            />
          </InputContainer>
        </Inputs>

        <SubmitButton
          size="xsmall"
          rounded
          icon="end"
          type="submit"
          disabled={errorsInForm}
        >
          {t('forgot_password_button_submit')}
          <SubmitIcon aria-hidden />
        </SubmitButton>

        {forgotPasswordError && (
          <ErrorBlock>{forgotPasswordError.message}</ErrorBlock>
        )}
      </form>
    </Container>
  );
}
